import React, { useEffect, useState } from 'react';
import { Formik, Form, FieldArray } from 'formik';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  Divider,
  IconButton,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import axios from '../../config/axios';
import { useProject } from '../../context/ProjectContext';
import { useLocation } from 'react-router-dom';
import { autoPopulateSapFromProtocol, fetchLatestSAPIntakeData } from '../services/sapIntake.service';

// All SAP fields grouped under sap_content for backend compatibility
const defaultSapContent = {
  protocol_id: '',
  protocol_version: '',
  sponsor_name: '',
  study_phase: '',
  indication: '',
  statistician_contact: '',
  primary_objective: '',
  secondary_objectives: [''],
  estimand_primary: {
    population: '',
    treatment: '',
    variable: '',
    intercurrent_strategy: '',
    summary_measure: '',
  },
  estimand_secondary: [
    {
      population: '',
      treatment: '',
      variable: '',
      intercurrent_strategy: '',
      summary_measure: '',
    },
  ],
  design_type: '',
  n_arms: '',
  control_type: '',
  randomisation_ratio: '',
  strat_factors: [''],
  blinding_level: '',
  visit_schedule_ref: '',
  itt_definition: '',
  pp_definition: '',
  safety_pop_definition: '',
  other_sets: [''],
  primary_endpoint: '',
  effect_size: '',
  assumed_control_mean: '',
  alpha_two_sided: '',
  power: '',
  dropout_rate: '',
  calc_method: '',
  interim_planned: '',
  interim_timing: '',
  alpha_spend_func: '',
  dmc_involved: '',
  primary_model: '',
  covariates: [''],
  sensitivity_methods: [''],
  subgroup_list: [''],
  mult_strategy: '',
  hierarchy_order: [''],
  general_missing: '',
  imputation_method: '',
  tipping_point_flag: '',
  ae_coding_dict: '',
  teae_window: '',
  lab_grade_scale: '',
  pk_parameters: [''],
  pd_endpoints: [''],
  pro_instruments: [''],
  scoring_algorithm_ref: '',
  analysis_software: '',
  adam_version: '',
  tfl_shell_ref: '',
  double_prog: '',
  qc_level: '',
  prior_sap_version: '',
  change_reason: '',
};

function normalizeArrayField(val) {
  if (Array.isArray(val)) {
    // Replace 'Not Provided' or falsy with ''
    return val.length > 0 ? val.map(v => (!v || v === "Not Provided" ? "" : v)) : [""];
  }
  if (!val || val === "Not Provided") return [""];
  return [val];
}

function normalizeSapContent(sap_content) {
  return {
    ...sap_content,
    secondary_objectives: normalizeArrayField(sap_content.secondary_objectives),
    strat_factors: normalizeArrayField(sap_content.strat_factors),
    other_sets: normalizeArrayField(sap_content.other_sets),
    covariates: normalizeArrayField(sap_content.covariates),
    sensitivity_methods: normalizeArrayField(sap_content.sensitivity_methods),
    subgroup_list: normalizeArrayField(sap_content.subgroup_list),
    hierarchy_order: normalizeArrayField(sap_content.hierarchy_order),
    pk_parameters: normalizeArrayField(sap_content.pk_parameters),
    pd_endpoints: normalizeArrayField(sap_content.pd_endpoints),
    pro_instruments: normalizeArrayField(sap_content.pro_instruments),
    // For estimand_secondary, ensure at least one object with empty fields
    estimand_secondary: Array.isArray(sap_content.estimand_secondary) && sap_content.estimand_secondary.length > 0
      ? sap_content.estimand_secondary.map(est => ({
          population: est.population || "",
          treatment: est.treatment || "",
          variable: est.variable || "",
          intercurrent_strategy: est.intercurrent_strategy || "",
          summary_measure: est.summary_measure || "",
        }))
      : [{
          population: "",
          treatment: "",
          variable: "",
          intercurrent_strategy: "",
          summary_measure: "",
        }],
  };
}

function getInitialValues(initialData) {
  // If initialData.output exists, parse it as sap_content
  let sap_content = { ...defaultSapContent };
  if (initialData && initialData.output) {
    try {
      const parsed = typeof initialData.output === 'string' ? JSON.parse(initialData.output) : initialData.output;
      sap_content = { ...sap_content, ...parsed };
    } catch (e) {
      // fallback to defaultSapContent
    }
  }
  // Defensive: always return a valid sap_content object
  if (!sap_content || typeof sap_content !== 'object') sap_content = { ...defaultSapContent };
  return { sap_content: normalizeSapContent(sap_content) };
}

function mapSapDocumentToSapContent(doc = {}) {
  const mapped = { ...defaultSapContent };
  Object.keys(mapped).forEach(key => {
    if (Object.prototype.hasOwnProperty.call(doc, key) && doc[key] !== undefined) {
      mapped[key] = doc[key];
    }
  });
  return mapped;
}

const SAP_UPLOAD_AND_EXTRACT_ENDPOINT = process.env.REACT_APP_API_URL + '/api/study-protocols/sap-intake/upload-and-extract';

function OperationInputForm({ onSubmit, initialData = {}, loading = false, onSuccess }) {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const projectIdFromUrl = queryParams.get('projectId');
  const { currentProject } = useProject();
  const actualProjectId = currentProject?._id || projectIdFromUrl;
  const [resolvedInitialData, setResolvedInitialData] = useState(initialData);

  useEffect(() => {
    setResolvedInitialData(initialData);
  }, [initialData]);

  // On submit, wrap sap_content as output (stringified)
  const handleSubmit = async (values) => {
    const output = JSON.stringify(values.sap_content);
    const submitData = {
      ...initialData,
      output,
      project: actualProjectId, // Ensure project is sent as a top-level field
    };
    let submissionSucceeded = false;
    try {
      if (onSubmit) {
        await onSubmit(submitData); // Wait for backend save
      }
      submissionSucceeded = true;
      setUploadError(null);
      if (onSuccess) onSuccess();   // Only switch tab if save succeeded
    } catch (err) {
      setUploadError(err.message || 'Failed to save SAP Intake Form');
    } finally {
      if (submissionSucceeded && actualProjectId) {
        const key = `sap-intake-lock-${actualProjectId}`;
        if (typeof window !== 'undefined') {
          localStorage.setItem(key, 'true');
        }
        setIsLocked(true);
        setAutoPopulateMessage('This SAP intake form has been submitted and is now locked.');
      }
    }
  };

  const [protocolFile, setProtocolFile] = useState(null);
  const [protocolFileInfo, setProtocolFileInfo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [sapIntakeId, setSapIntakeId] = useState(null);
  const [autoPopulating, setAutoPopulating] = useState(false);
  const [autoPopulateMessage, setAutoPopulateMessage] = useState('');
  const [isLocked, setIsLocked] = useState(false);

  useEffect(() => {
     if (!actualProjectId) {
       setIsLocked(false);
       setAutoPopulateMessage('');
       return;
     }
     const key = `sap-intake-lock-${actualProjectId}`;
     const stored = typeof window !== 'undefined' ? localStorage.getItem(key) : null;
     const locked = stored === 'true';
     setIsLocked(locked);
     if (locked) {
       setAutoPopulateMessage('This SAP intake form has been submitted and is now locked.');
     } else {
       setAutoPopulateMessage('');
     }
   }, [actualProjectId]);

  useEffect(() => {
    let isMounted = true;
    const loadLatestSapIntake = async () => {
      if (!actualProjectId) {
        return;
      }
      try {
        const latest = await fetchLatestSAPIntakeData(actualProjectId);
        if (!isMounted || !latest) {
          return;
        }
        const sapContent = mapSapDocumentToSapContent(latest);
        const normalizedOutput = latest.output || JSON.stringify(sapContent);
        setResolvedInitialData({ ...latest, output: normalizedOutput });
        if (latest.fileInfo) {
          setProtocolFileInfo(latest.fileInfo);
        }
        if (latest._id) {
          setSapIntakeId(latest._id);
        }
        const key = `sap-intake-lock-${actualProjectId}`;
        if (typeof window !== 'undefined' && normalizedOutput) {
          localStorage.setItem(key, 'true');
          setIsLocked(true);
          setAutoPopulateMessage('This SAP intake form has been submitted and is now locked.');
        }
      } catch (err) {
        console.error('Failed to load latest SAP intake form:', err);
      }
    };

    loadLatestSapIntake();

    return () => {
      isMounted = false;
    };
  }, [actualProjectId]);

  const handleProtocolFileChange = (e) => {
    if (isLocked) {
      return;
    }
    const file = e.target.files[0];
    if (!file) return;
    setProtocolFile(file);
    setProtocolFileInfo(null);
    setUploadError(null);
    setAutoPopulateMessage('');
  };

  const handleAutoPopulateFromProtocol = async (setValues) => {
    if (!actualProjectId || actualProjectId === 'null') {
      setUploadError('Project ID is missing. Please select a project.');
      return;
    }

    if (isLocked) {
      setAutoPopulateMessage('Form is locked and cannot be auto-populated.');
      return;
    }

    setAutoPopulating(true);
    setUploadError(null);
    setAutoPopulateMessage('');

    try {
      const response = await autoPopulateSapFromProtocol(actualProjectId);
      if (response?.success && response.data) {
        const {
          _id: newSapIntakeId,
          project,
          createdAt,
          updatedAt,
          fileInfo: responseFileInfo,
          __v,
          ...sapFields
        } = response.data;

        setSapIntakeId(newSapIntakeId || null);
        setProtocolFile(null);
        setProtocolFileInfo(responseFileInfo || null);

        setValues((prev) => ({
          ...prev,
          sap_content: normalizeSapContent({
            ...defaultSapContent,
            ...sapFields,
          }),
        }));

        setAutoPopulateMessage('Auto-populated from stored protocol sections. Review and adjust as needed.');
      } else {
        setUploadError(response?.error || 'Auto-populate failed.');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Auto-populate failed.';
      setUploadError(errorMessage);
    } finally {
      setAutoPopulating(false);
    }
  };

  const handleDoneClick = async (setValues) => {
    if (isLocked) {
      setUploadError('Form is locked and cannot accept uploads.');
      return;
    }
    if (!protocolFile) {
      setUploadError('Please select a file first.');
      return;
    }
    if (!actualProjectId || actualProjectId === 'null') {
      setUploadError('Project ID is missing. Please select a project.');
      setUploading(false);
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', protocolFile);
      formData.append('projectId', actualProjectId);
      const response = await axios.post(SAP_UPLOAD_AND_EXTRACT_ENDPOINT, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data.success) {
        setProtocolFileInfo(response.data.data.fileInfo);
        setSapIntakeId(response.data.data._id);
        // Auto-populate the form with the returned SAP Intake Form data
        setValues((prev) => ({
          ...prev,
          sap_content: normalizeSapContent({
            ...prev.sap_content,
            ...response.data.data,
          }),
        }));
      } else {
        setUploadError(response.data.error || 'Extraction failed');
      }
    } catch (err) {
      setUploadError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Formik
      initialValues={getInitialValues(resolvedInitialData)}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ values, handleChange, handleBlur, setValues }) => {
        // Defensive: always ensure values.sap_content exists
        const safeValues = {
          sap_content: { ...defaultSapContent, ...(values.sap_content || {}) }
        };
        return (
          <Form>
            {isLocked && (
              <Box sx={{ mb: 3 }}>
                <Typography color="warning.main" fontWeight={600}>
                  This SAP intake form has been submitted and is now locked. Editing and uploads are disabled.
                </Typography>
              </Box>
            )}
            <Alert severity="info" sx={{ mb: 2 }}>
              Your SAP is taking shape....
            </Alert>
            <fieldset disabled={isLocked} style={{ border: 'none', margin: 0, padding: 0 }}>
              <Box sx={{
                border: '2px solid #1976d2',
                borderRadius: 4,
                p: 4,
                background: 'linear-gradient(135deg, #f8fafc 60%, #e3f2fd 100%)',
                boxShadow: '0 8px 32px 0 rgba(25, 118, 210, 0.18), 0 1.5px 0 #fff inset',
                mb: 4,
                position: 'relative',
                overflow: 'visible',
              }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Protocol Upload</Typography>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleProtocolFileChange}
                  disabled={uploading || isLocked}
                />
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mt: 2, ml: 2 }}
                  onClick={() => handleDoneClick(setValues)}
                  disabled={uploading || !protocolFile || isLocked}
                >
                  {uploading ? 'Processing...' : 'Done'}
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  sx={{ mt: 2, ml: 2 }}
                  onClick={() => handleAutoPopulateFromProtocol(setValues)}
                  disabled={autoPopulating || isLocked}
                >
                  {autoPopulating ? 'Auto-populating...' : 'Auto-populate from Protocol'}
                </Button>
                {uploading && <Typography color="info.main">Uploading and extracting...</Typography>}
                {uploadError && <Typography color="error.main">{uploadError}</Typography>}
                {autoPopulateMessage && !uploadError && (
                  <Typography color="success.main">{autoPopulateMessage}</Typography>
                )}
                {protocolFileInfo && (
                  <Typography color="success.main">
                    Source:{' '}
                    {protocolFileInfo.azureUrl ? (
                      <a href={protocolFileInfo.azureUrl} target="_blank" rel="noopener noreferrer">{protocolFileInfo.originalName}</a>
                    ) : (
                      protocolFileInfo.originalName || 'Stored protocol sections'
                    )}
                  </Typography>
                )}
              </Box>

            {/* Section A: Administrative */}
            <Box sx={{
              border: '1.5px solid #90caf9',
              borderRadius: 3,
              background: 'linear-gradient(120deg, #fff 80%, #e3f2fd 100%)',
              p: 3,
              mb: 4,
              boxShadow: '0 4px 24px 0 rgba(25, 118, 210, 0.12), 0 1.5px 0 #fff inset',
              position: 'relative',
              transition: 'box-shadow 0.2s',
              '&:hover': {
                boxShadow: '0 12px 32px 0 rgba(25, 118, 210, 0.22), 0 2px 0 #fff inset',
                transform: 'translateY(-2px) scale(1.01)',
              },
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>A) Administrative</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth name="sap_content.protocol_id" label="Short title / protocol number" value={safeValues.sap_content.protocol_id} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth name="sap_content.protocol_version" label="Version and date" value={safeValues.sap_content.protocol_version} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth name="sap_content.sponsor_name" label="Legal sponsor name" value={safeValues.sap_content.sponsor_name} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth name="sap_content.study_phase" label="Phase I, II, III, IV" value={safeValues.sap_content.study_phase} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth name="sap_content.indication" label="Disease / condition" value={safeValues.sap_content.indication} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth name="sap_content.statistician_contact" label="Name & email of lead statistician" value={safeValues.sap_content.statistician_contact} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
              </Grid>
            </Box>

            {/* Section B: Objectives & Estimands */}
            <Box sx={{
              border: '1.5px solid #90caf9',
              borderRadius: 3,
              background: 'linear-gradient(120deg, #fff 80%, #e3f2fd 100%)',
              p: 3,
              mb: 4,
              boxShadow: '0 4px 24px 0 rgba(25, 118, 210, 0.12), 0 1.5px 0 #fff inset',
              position: 'relative',
              transition: 'box-shadow 0.2s',
              '&:hover': {
                boxShadow: '0 12px 32px 0 rgba(25, 118, 210, 0.22), 0 2px 0 #fff inset',
                transform: 'translateY(-2px) scale(1.01)',
              },
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>B) Objectives & Estimands</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField fullWidth name="sap_content.primary_objective" label="Narrative primary objective" value={safeValues.sap_content.primary_objective} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12}>
                  <FieldArray name="sap_content.secondary_objectives">
                    {({ push, remove }) => (
                      <Box>
                        <Typography variant="subtitle1">List of secondary objectives</Typography>
                        {safeValues.sap_content.secondary_objectives.map((obj, idx) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <TextField
                              fullWidth
                              name={`sap_content.secondary_objectives[${idx}]`}
                              value={obj}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              label={`Secondary Objective #${idx + 1}`}
                            />
                            <IconButton onClick={() => remove(idx)} disabled={safeValues.sap_content.secondary_objectives.length === 1}><RemoveIcon /></IconButton>
                            <IconButton onClick={() => push('')}><AddIcon /></IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </FieldArray>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth name="sap_content.estimand_primary.population" label="Target population" value={safeValues.sap_content.estimand_primary.population} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth name="sap_content.estimand_primary.treatment" label="Treatment arms compared" value={safeValues.sap_content.estimand_primary.treatment} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth name="sap_content.estimand_primary.variable" label="Endpoint variable" value={safeValues.sap_content.estimand_primary.variable} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth name="sap_content.estimand_primary.intercurrent_strategy" label="Strategy for intercurrent events" value={safeValues.sap_content.estimand_primary.intercurrent_strategy} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth name="sap_content.estimand_primary.summary_measure" label="Effect measure (mean diff, HR, OR)" value={safeValues.sap_content.estimand_primary.summary_measure} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12}>
                  <FieldArray name="sap_content.estimand_secondary">
                    {({ push, remove }) => (
                      <Box>
                        <Typography variant="subtitle1">Key secondary estimands</Typography>
                        {safeValues.sap_content.estimand_secondary.map((est, idx) => (
                          <Grid container spacing={1} key={idx} sx={{ mb: 1 }}>
                            <Grid item xs={12} md={2}>
                              <TextField fullWidth name={`sap_content.estimand_secondary[${idx}].population`} label="Population" value={est.population} onChange={handleChange} onBlur={handleBlur} />
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <TextField fullWidth name={`sap_content.estimand_secondary[${idx}].treatment`} label="Treatment" value={est.treatment} onChange={handleChange} onBlur={handleBlur} />
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <TextField fullWidth name={`sap_content.estimand_secondary[${idx}].variable`} label="Variable" value={est.variable} onChange={handleChange} onBlur={handleBlur} />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField fullWidth name={`sap_content.estimand_secondary[${idx}].intercurrent_strategy`} label="Intercurrent Strategy" value={est.intercurrent_strategy} onChange={handleChange} onBlur={handleBlur} />
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <TextField fullWidth name={`sap_content.estimand_secondary[${idx}].summary_measure`} label="Summary Measure" value={est.summary_measure} onChange={handleChange} onBlur={handleBlur} />
                            </Grid>
                            <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
                              <IconButton onClick={() => remove(idx)} disabled={safeValues.sap_content.estimand_secondary.length === 1}><RemoveIcon /></IconButton>
                              <IconButton onClick={() => push({ population: '', treatment: '', variable: '', intercurrent_strategy: '', summary_measure: '' })}><AddIcon /></IconButton>
                            </Grid>
                          </Grid>
                        ))}
                      </Box>
                    )}
                  </FieldArray>
                </Grid>
              </Grid>
            </Box>

            {/* Section C: Study Design & Randomisation */}
            <Box sx={{
              border: '1.5px solid #90caf9',
              borderRadius: 3,
              background: 'linear-gradient(120deg, #fff 80%, #e3f2fd 100%)',
              p: 3,
              mb: 4,
              boxShadow: '0 4px 24px 0 rgba(25, 118, 210, 0.12), 0 1.5px 0 #fff inset',
              position: 'relative',
              transition: 'box-shadow 0.2s',
              '&:hover': {
                boxShadow: '0 12px 32px 0 rgba(25, 118, 210, 0.22), 0 2px 0 #fff inset',
                transform: 'translateY(-2px) scale(1.01)',
              },
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>C) Study Design & Randomisation</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.design_type" label="Parallel, crossover, etc." value={safeValues.sap_content.design_type} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.n_arms" label="Number of study arms" value={safeValues.sap_content.n_arms} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.control_type" label="Placebo, active, historical" value={safeValues.sap_content.control_type} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.randomisation_ratio" label="Allocation ratio (e.g., 1:1)" value={safeValues.sap_content.randomisation_ratio} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FieldArray name="sap_content.strat_factors">
                    {({ push, remove }) => (
                      <Box>
                        <Typography variant="subtitle1">Stratification factors & levels</Typography>
                        {safeValues.sap_content.strat_factors.map((factor, idx) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <TextField
                              fullWidth
                              name={`sap_content.strat_factors[${idx}]`}
                              value={factor}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              label={`Stratification Factor #${idx + 1}`}
                            />
                            <IconButton onClick={() => remove(idx)} disabled={safeValues.sap_content.strat_factors.length === 1}><RemoveIcon /></IconButton>
                            <IconButton onClick={() => push('')}><AddIcon /></IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </FieldArray>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.blinding_level" label="Double-blind, open-label, etc." value={safeValues.sap_content.blinding_level} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.visit_schedule_ref" label="Reference to visit schedule table" value={safeValues.sap_content.visit_schedule_ref} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
              </Grid>
            </Box>

            {/* Section D: Analysis Populations */}
            <Box sx={{
              border: '1.5px solid #90caf9',
              borderRadius: 3,
              background: 'linear-gradient(120deg, #fff 80%, #e3f2fd 100%)',
              p: 3,
              mb: 4,
              boxShadow: '0 4px 24px 0 rgba(25, 118, 210, 0.12), 0 1.5px 0 #fff inset',
              position: 'relative',
              transition: 'box-shadow 0.2s',
              '&:hover': {
                boxShadow: '0 12px 32px 0 rgba(25, 118, 210, 0.22), 0 2px 0 #fff inset',
                transform: 'translateY(-2px) scale(1.01)',
              },
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>D) Analysis Populations</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.itt_definition" label="ITT population definition" value={safeValues.sap_content.itt_definition} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.pp_definition" label="Per-protocol definition" value={safeValues.sap_content.pp_definition} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.safety_pop_definition" label="Safety population definition" value={safeValues.sap_content.safety_pop_definition} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12}>
                  <FieldArray name="sap_content.other_sets">
                    {({ push, remove }) => (
                      <Box>
                        <Typography variant="subtitle1">Other analysis sets (PK, etc.)</Typography>
                        {safeValues.sap_content.other_sets.map((set, idx) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <TextField
                              fullWidth
                              name={`sap_content.other_sets[${idx}]`}
                              value={set}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              label={`Other Set #${idx + 1}`}
                            />
                            <IconButton onClick={() => remove(idx)} disabled={safeValues.sap_content.other_sets.length === 1}><RemoveIcon /></IconButton>
                            <IconButton onClick={() => push('')}><AddIcon /></IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </FieldArray>
                </Grid>
              </Grid>
            </Box>

            {/* Section E: Sample Size & Power */}
            <Box sx={{
              border: '1.5px solid #90caf9',
              borderRadius: 3,
              background: 'linear-gradient(120deg, #fff 80%, #e3f2fd 100%)',
              p: 3,
              mb: 4,
              boxShadow: '0 4px 24px 0 rgba(25, 118, 210, 0.12), 0 1.5px 0 #fff inset',
              position: 'relative',
              transition: 'box-shadow 0.2s',
              '&:hover': {
                boxShadow: '0 12px 32px 0 rgba(25, 118, 210, 0.22), 0 2px 0 #fff inset',
                transform: 'translateY(-2px) scale(1.01)',
              },
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>E) Sample Size & Power</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.primary_endpoint" label="Primary endpoint short name" value={safeValues.sap_content.primary_endpoint} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.effect_size" label="Expected effect size" value={safeValues.sap_content.effect_size} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.assumed_control_mean" label="Control mean / event rate" value={safeValues.sap_content.assumed_control_mean} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.alpha_two_sided" label="Type I error (two-sided)" value={safeValues.sap_content.alpha_two_sided} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.power" label="Statistical power" value={safeValues.sap_content.power} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.dropout_rate" label="Anticipated dropout (%)" value={safeValues.sap_content.dropout_rate} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.calc_method" label="Software / method used" value={safeValues.sap_content.calc_method} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
              </Grid>
            </Box>

            {/* Section F: Interim Monitoring */}
            <Box sx={{
              border: '1.5px solid #90caf9',
              borderRadius: 3,
              background: 'linear-gradient(120deg, #fff 80%, #e3f2fd 100%)',
              p: 3,
              mb: 4,
              boxShadow: '0 4px 24px 0 rgba(25, 118, 210, 0.12), 0 1.5px 0 #fff inset',
              position: 'relative',
              transition: 'box-shadow 0.2s',
              '&:hover': {
                boxShadow: '0 12px 32px 0 rgba(25, 118, 210, 0.22), 0 2px 0 #fff inset',
                transform: 'translateY(-2px) scale(1.01)',
              },
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>F) Interim Monitoring</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth name="sap_content.interim_planned" label="Interim analysis planned?" value={safeValues.sap_content.interim_planned} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth name="sap_content.interim_timing" label="Information fraction / timing" value={safeValues.sap_content.interim_timing} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth name="sap_content.alpha_spend_func" label="Alpha-spending function" value={safeValues.sap_content.alpha_spend_func} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth name="sap_content.dmc_involved" label="Data Monitoring Committee?" value={safeValues.sap_content.dmc_involved} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
              </Grid>
            </Box>

            {/* Section G: Efficacy Analysis Choices */}
            <Box sx={{
              border: '1.5px solid #90caf9',
              borderRadius: 3,
              background: 'linear-gradient(120deg, #fff 80%, #e3f2fd 100%)',
              p: 3,
              mb: 4,
              boxShadow: '0 4px 24px 0 rgba(25, 118, 210, 0.12), 0 1.5px 0 #fff inset',
              position: 'relative',
              transition: 'box-shadow 0.2s',
              '&:hover': {
                boxShadow: '0 12px 32px 0 rgba(25, 118, 210, 0.22), 0 2px 0 #fff inset',
                transform: 'translateY(-2px) scale(1.01)',
              },
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>G) Efficacy Analysis Choices</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.primary_model" label="Analysis model (ANCOVA, Cox)" value={safeValues.sap_content.primary_model} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FieldArray name="sap_content.covariates">
                    {({ push, remove }) => (
                      <Box>
                        <Typography variant="subtitle1">Covariates in primary model</Typography>
                        {safeValues.sap_content.covariates.map((cov, idx) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <TextField
                              fullWidth
                              name={`sap_content.covariates[${idx}]`}
                              value={cov}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              label={`Covariate #${idx + 1}`}
                            />
                            <IconButton onClick={() => remove(idx)} disabled={safeValues.sap_content.covariates.length === 1}><RemoveIcon /></IconButton>
                            <IconButton onClick={() => push('')}><AddIcon /></IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </FieldArray>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FieldArray name="sap_content.sensitivity_methods">
                    {({ push, remove }) => (
                      <Box>
                        <Typography variant="subtitle1">Planned sensitivity analyses</Typography>
                        {safeValues.sap_content.sensitivity_methods.map((method, idx) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <TextField
                              fullWidth
                              name={`sap_content.sensitivity_methods[${idx}]`}
                              value={method}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              label={`Sensitivity Method #${idx + 1}`}
                            />
                            <IconButton onClick={() => remove(idx)} disabled={safeValues.sap_content.sensitivity_methods.length === 1}><RemoveIcon /></IconButton>
                            <IconButton onClick={() => push('')}><AddIcon /></IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </FieldArray>
                </Grid>
                <Grid item xs={12}>
                  <FieldArray name="sap_content.subgroup_list">
                    {({ push, remove }) => (
                      <Box>
                        <Typography variant="subtitle1">Pre-specified subgroups</Typography>
                        {safeValues.sap_content.subgroup_list.map((sg, idx) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <TextField
                              fullWidth
                              name={`sap_content.subgroup_list[${idx}]`}
                              value={sg}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              label={`Subgroup #${idx + 1}`}
                            />
                            <IconButton onClick={() => remove(idx)} disabled={safeValues.sap_content.subgroup_list.length === 1}><RemoveIcon /></IconButton>
                            <IconButton onClick={() => push('')}><AddIcon /></IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </FieldArray>
                </Grid>
              </Grid>
            </Box>

            {/* Section H: Multiplicity Control */}
            <Box sx={{
              border: '1.5px solid #90caf9',
              borderRadius: 3,
              background: 'linear-gradient(120deg, #fff 80%, #e3f2fd 100%)',
              p: 3,
              mb: 4,
              boxShadow: '0 4px 24px 0 rgba(25, 118, 210, 0.12), 0 1.5px 0 #fff inset',
              position: 'relative',
              transition: 'box-shadow 0.2s',
              '&:hover': {
                boxShadow: '0 12px 32px 0 rgba(25, 118, 210, 0.22), 0 2px 0 #fff inset',
                transform: 'translateY(-2px) scale(1.01)',
              },
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>H) Multiplicity Control</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth name="sap_content.mult_strategy" label="Multiplicity adjustment strategy" value={safeValues.sap_content.mult_strategy} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldArray name="sap_content.hierarchy_order">
                    {({ push, remove }) => (
                      <Box>
                        <Typography variant="subtitle1">Ordered list of hypotheses</Typography>
                        {safeValues.sap_content.hierarchy_order.map((h, idx) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <TextField
                              fullWidth
                              name={`sap_content.hierarchy_order[${idx}]`}
                              value={h}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              label={`Hypothesis #${idx + 1}`}
                            />
                            <IconButton onClick={() => remove(idx)} disabled={safeValues.sap_content.hierarchy_order.length === 1}><RemoveIcon /></IconButton>
                            <IconButton onClick={() => push('')}><AddIcon /></IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </FieldArray>
                </Grid>
              </Grid>
            </Box>

            {/* Section I: Missing-Data Handling */}
            <Box sx={{
              border: '1.5px solid #90caf9',
              borderRadius: 3,
              background: 'linear-gradient(120deg, #fff 80%, #e3f2fd 100%)',
              p: 3,
              mb: 4,
              boxShadow: '0 4px 24px 0 rgba(25, 118, 210, 0.12), 0 1.5px 0 #fff inset',
              position: 'relative',
              transition: 'box-shadow 0.2s',
              '&:hover': {
                boxShadow: '0 12px 32px 0 rgba(25, 118, 210, 0.22), 0 2px 0 #fff inset',
                transform: 'translateY(-2px) scale(1.01)',
              },
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>I) Missing-Data Handling</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.general_missing" label="Overall missing-data approach" value={safeValues.sap_content.general_missing} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.imputation_method" label="Imputation method" value={safeValues.sap_content.imputation_method} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.tipping_point_flag" label="Tipping-point analysis planned?" value={safeValues.sap_content.tipping_point_flag} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
              </Grid>
            </Box>

            {/* Section J: Safety Analysis Choices */}
            <Box sx={{
              border: '1.5px solid #90caf9',
              borderRadius: 3,
              background: 'linear-gradient(120deg, #fff 80%, #e3f2fd 100%)',
              p: 3,
              mb: 4,
              boxShadow: '0 4px 24px 0 rgba(25, 118, 210, 0.12), 0 1.5px 0 #fff inset',
              position: 'relative',
              transition: 'box-shadow 0.2s',
              '&:hover': {
                boxShadow: '0 12px 32px 0 rgba(25, 118, 210, 0.22), 0 2px 0 #fff inset',
                transform: 'translateY(-2px) scale(1.01)',
              },
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>J) Safety Analysis Choices</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.ae_coding_dict" label="AE coding dictionary (MedDRA vX)" value={safeValues.sap_content.ae_coding_dict} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.teae_window" label="TEAE start/stop rules" value={safeValues.sap_content.teae_window} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.lab_grade_scale" label="Lab toxicity grade scale" value={safeValues.sap_content.lab_grade_scale} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
              </Grid>
            </Box>

            {/* Section K: PK/PD & Biomarkers */}
            <Box sx={{
              border: '1.5px solid #90caf9',
              borderRadius: 3,
              background: 'linear-gradient(120deg, #fff 80%, #e3f2fd 100%)',
              p: 3,
              mb: 4,
              boxShadow: '0 4px 24px 0 rgba(25, 118, 210, 0.12), 0 1.5px 0 #fff inset',
              position: 'relative',
              transition: 'box-shadow 0.2s',
              '&:hover': {
                boxShadow: '0 12px 32px 0 rgba(25, 118, 210, 0.22), 0 2px 0 #fff inset',
                transform: 'translateY(-2px) scale(1.01)',
              },
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>K) PK/PD & Biomarkers</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FieldArray name="sap_content.pk_parameters">
                    {({ push, remove }) => (
                      <Box>
                        <Typography variant="subtitle1">PK parameters (AUC, Cmax, …)</Typography>
                        {safeValues.sap_content.pk_parameters.map((pk, idx) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <TextField
                              fullWidth
                              name={`sap_content.pk_parameters[${idx}]`}
                              value={pk}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              label={`PK Parameter #${idx + 1}`}
                            />
                            <IconButton onClick={() => remove(idx)} disabled={safeValues.sap_content.pk_parameters.length === 1}><RemoveIcon /></IconButton>
                            <IconButton onClick={() => push('')}><AddIcon /></IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </FieldArray>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldArray name="sap_content.pd_endpoints">
                    {({ push, remove }) => (
                      <Box>
                        <Typography variant="subtitle1">PD or biomarker endpoints</Typography>
                        {safeValues.sap_content.pd_endpoints.map((pd, idx) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <TextField
                              fullWidth
                              name={`sap_content.pd_endpoints[${idx}]`}
                              value={pd}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              label={`PD Endpoint #${idx + 1}`}
                            />
                            <IconButton onClick={() => remove(idx)} disabled={safeValues.sap_content.pd_endpoints.length === 1}><RemoveIcon /></IconButton>
                            <IconButton onClick={() => push('')}><AddIcon /></IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </FieldArray>
                </Grid>
              </Grid>
            </Box>

            {/* Section L: PRO / HEOR */}
            <Box sx={{
              border: '1.5px solid #90caf9',
              borderRadius: 3,
              background: 'linear-gradient(120deg, #fff 80%, #e3f2fd 100%)',
              p: 3,
              mb: 4,
              boxShadow: '0 4px 24px 0 rgba(25, 118, 210, 0.12), 0 1.5px 0 #fff inset',
              position: 'relative',
              transition: 'box-shadow 0.2s',
              '&:hover': {
                boxShadow: '0 12px 32px 0 rgba(25, 118, 210, 0.22), 0 2px 0 #fff inset',
                transform: 'translateY(-2px) scale(1.01)',
              },
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>L) PRO / HEOR</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FieldArray name="sap_content.pro_instruments">
                    {({ push, remove }) => (
                      <Box>
                        <Typography variant="subtitle1">PRO instruments used</Typography>
                        {safeValues.sap_content.pro_instruments.map((pro, idx) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <TextField
                              fullWidth
                              name={`sap_content.pro_instruments[${idx}]`}
                              value={pro}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              label={`PRO Instrument #${idx + 1}`}
                            />
                            <IconButton onClick={() => remove(idx)} disabled={safeValues.sap_content.pro_instruments.length === 1}><RemoveIcon /></IconButton>
                            <IconButton onClick={() => push('')}><AddIcon /></IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </FieldArray>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth name="sap_content.scoring_algorithm_ref" label="Reference for scoring" value={safeValues.sap_content.scoring_algorithm_ref} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
              </Grid>
            </Box>

            {/* Section M: Programming & Standards */}
            <Box sx={{
              border: '1.5px solid #90caf9',
              borderRadius: 3,
              background: 'linear-gradient(120deg, #fff 80%, #e3f2fd 100%)',
              p: 3,
              mb: 4,
              boxShadow: '0 4px 24px 0 rgba(25, 118, 210, 0.12), 0 1.5px 0 #fff inset',
              position: 'relative',
              transition: 'box-shadow 0.2s',
              '&:hover': {
                boxShadow: '0 12px 32px 0 rgba(25, 118, 210, 0.22), 0 2px 0 #fff inset',
                transform: 'translateY(-2px) scale(1.01)',
              },
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>M) Programming & Standards</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.analysis_software" label="SAS, R, etc." value={safeValues.sap_content.analysis_software} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.adam_version" label="ADaM implementation guide version" value={safeValues.sap_content.adam_version} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth name="sap_content.tfl_shell_ref" label="Location of TLF shells" value={safeValues.sap_content.tfl_shell_ref} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
              </Grid>
            </Box>

            {/* Section N: QC / Traceability */}
            <Box sx={{
              border: '1.5px solid #90caf9',
              borderRadius: 3,
              background: 'linear-gradient(120deg, #fff 80%, #e3f2fd 100%)',
              p: 3,
              mb: 4,
              boxShadow: '0 4px 24px 0 rgba(25, 118, 210, 0.12), 0 1.5px 0 #fff inset',
              position: 'relative',
              transition: 'box-shadow 0.2s',
              '&:hover': {
                boxShadow: '0 12px 32px 0 rgba(25, 118, 210, 0.22), 0 2px 0 #fff inset',
                transform: 'translateY(-2px) scale(1.01)',
              },
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>N) QC / Traceability</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth name="sap_content.double_prog" label="Double programming?" value={safeValues.sap_content.double_prog} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth name="sap_content.qc_level" label="Level of QC (100 %, risk-based)" value={safeValues.sap_content.qc_level} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
              </Grid>
            </Box>

            {/* Section O: Change Log */}
            <Box sx={{
              border: '1.5px solid #90caf9',
              borderRadius: 3,
              background: 'linear-gradient(120deg, #fff 80%, #e3f2fd 100%)',
              p: 3,
              mb: 4,
              boxShadow: '0 4px 24px 0 rgba(25, 118, 210, 0.12), 0 1.5px 0 #fff inset',
              position: 'relative',
              transition: 'box-shadow 0.2s',
              '&:hover': {
                boxShadow: '0 12px 32px 0 rgba(25, 118, 210, 0.22), 0 2px 0 #fff inset',
                transform: 'translateY(-2px) scale(1.01)',
              },
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>O) Change Log</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth name="sap_content.prior_sap_version" label="Previous SAP version" value={safeValues.sap_content.prior_sap_version} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth name="sap_content.change_reason" label="Reason for changes" value={safeValues.sap_content.change_reason} onChange={handleChange} onBlur={handleBlur} />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 4 }} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
              <Button type="submit" variant="contained" color="primary" disabled={loading || isLocked}>
                {loading ? 'Saving...' : 'Submit'}
              </Button>
            </Box>
            </fieldset>
          </Form>
        );
      }}
    </Formik>
  );
};

export default OperationInputForm; 